import { useCallback, useMemo } from 'react'
import { LocalStorage } from '~/types/LocalStorage'
import { LocalStorageService } from '~/services/storage'
import type { Request as SavedRequest } from '~/types/SavedDesign'

export interface LocalMyDesign {
  id: string
  sourceMyDesignId?: string
  product: { id: string; version: string }
  request: SavedRequest
  savedAt: string
  updatedAt: string
}

const MAX_ITEMS_TOTAL = 100
const MAX_ITEMS_PER_PRODUCT = 20
const TTL_MS = 30 * 24 * 60 * 60 * 1000

function nowISO(): string {
  return new Date().toISOString()
}

const storage = new LocalStorageService<LocalMyDesign[]>(
  LocalStorage.MyDesignLocalV1
)

function readAll(): LocalMyDesign[] {
  const parsed = storage.get() || []
  if (!Array.isArray(parsed)) return []
  const now = Date.now()
  return parsed.filter((x) => now - Date.parse(x.updatedAt) <= TTL_MS)
}

function writeAll(items: LocalMyDesign[]): void {
  storage.set(items)
}

function evictIfNeeded(
  items: LocalMyDesign[],
  candidate?: LocalMyDesign
): LocalMyDesign[] {
  let result = items
  const byUpdatedAsc = (a: LocalMyDesign, b: LocalMyDesign) =>
    Date.parse(a.updatedAt) - Date.parse(b.updatedAt)

  // Per product cap
  if (candidate) {
    const sameProduct = result.filter(
      (x) =>
        x.product.id === candidate.product.id &&
        x.product.version === candidate.product.version
    )
    if (sameProduct.length >= MAX_ITEMS_PER_PRODUCT) {
      const toRemove = sameProduct.sort(byUpdatedAsc)[0]
      result = result.filter((x) => x.id !== toRemove.id)
    }
  }

  // Total cap
  while (result.length >= MAX_ITEMS_TOTAL) {
    result = result.sort(byUpdatedAsc).slice(1)
  }
  return result
}

export function useLocalMyDesigns() {
  const notifyUpdated = useCallback(() => {
    try {
      window.dispatchEvent(new Event('my-designs:updated'))
    } catch {
      /* noop */
    }
  }, [])

  const listAll = useCallback(async (): Promise<LocalMyDesign[]> => {
    return readAll().sort(
      (a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)
    )
  }, [])

  const listByProduct = useCallback(
    async (productId: string, version?: string): Promise<LocalMyDesign[]> => {
      const all = await listAll()
      return all.filter(
        (x) =>
          x.product.id === productId &&
          (version ? x.product.version === version : true)
      )
    },
    [listAll]
  )

  const save = useCallback(
    async (
      request: SavedRequest,
      product: { id: string; version: string },
      opts?: { sourceMyDesignId?: string }
    ): Promise<{ id: string }> => {
      const id =
        Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
      const item: LocalMyDesign = {
        id,
        sourceMyDesignId: opts?.sourceMyDesignId,
        product,
        request,
        savedAt: nowISO(),
        updatedAt: nowISO()
      }

      let items = readAll()
      items = evictIfNeeded(items, item)

      try {
        writeAll([...items, item])
        notifyUpdated()
      } catch (e) {
        // Quota exceeded fallback: remove oldest and retry once
        items = evictIfNeeded(
          items
            .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt))
            .slice(1),
          item
        )
        writeAll([...items, item])
        notifyUpdated()
      }

      return { id }
    },
    [notifyUpdated]
  )

  const update = useCallback(
    async (id: string, partial: Partial<LocalMyDesign>) => {
      const items = readAll()
      const updated = items.map((x) =>
        x.id === id ? { ...x, ...partial, updatedAt: nowISO() } : x
      )
      writeAll(updated)
      notifyUpdated()
    },
    [notifyUpdated]
  )

  const remove = useCallback(
    async (id: string) => {
      const items = readAll()
      writeAll(items.filter((x) => x.id !== id))
      notifyUpdated()
    },
    [notifyUpdated]
  )

  const clear = useCallback(async () => {
    storage.clear()
    notifyUpdated()
  }, [notifyUpdated])

  return useMemo(
    () => ({ listAll, listByProduct, save, update, remove, clear }),
    [listAll, listByProduct, save, update, remove, clear]
  )
}
